import type { FC } from 'react'
import React from 'react'
import { Link } from 'react-router-dom'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import type { Development } from '@homeviews/models'
import { Typography, StarRating } from '@homeviews/shared-ui'
import clsx from 'clsx'
import type { ITheme } from '@homeviews/styles'
import Skeleton from '@material-ui/lab/Skeleton'
import { mixpanelTrack } from '@homeviews/seo'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    cellEl: {
      display: 'flex',
    },
    cellElText: {},
    starRatingWrapper: {
      marginTop: 2,
    },
    cellElLink: {
      '& a': {
        color: theme.colors.primaryLink,
      },
    },
    title: {
      color: theme.colors.grey800,
    },
    ratingWrapper: {
      fontSize: 12,
      '& span': {
        color: '#434343',
        fontWeight: 500,
      },
    },
  }),
)

export const headCells: any = [
  {
    id: 'name',
    numeric: false,
    disablePadding: false,
    label: 'Development name',
  },
  {
    id: 'rating',
    numeric: true,
    disablePadding: false,
    label: 'Rating',
  },
  {
    id: 'developer',
    numeric: true,
    disablePadding: false,
    label: 'Developer',
  },
  {
    id: 'area',
    numeric: false,
    disablePadding: false,
    label: 'Area ranking',
  },
  {
    id: 'actions',
    numeric: false,
    disablePadding: false,
    label: 'Actions',
  },
]

interface IRatingCell {
  rating: string
  noReviews: number
  fontSize: number
}
const Rating: FC<IRatingCell> = ({ rating, noReviews }) => {
  const classes = useStyles()

  return (
    <div className={classes.cellEl}>
      <StarRating value={parseFloat(rating)} fontSize={16} />
      <Typography className={clsx(classes.cellElText, classes.ratingWrapper)}>
        <span style={{ marginLeft: '8px' }}>{rating} </span>{' '}
        {noReviews &&
          `(${noReviews} ${noReviews === 1 ? 'review' : 'reviews'})`}
      </Typography>
    </div>
  )
}

interface IAreaCell {
  area: string
  rankedOutOf: string
  currentRanking: string
}
const AreaRanking: FC<IAreaCell> = ({ area }) => {
  const classes = useStyles()

  return (
    <div className={classes.cellEl}>
      <Typography className={clsx(classes.cellElText)}>{area}</Typography>
    </div>
  )
}

interface IActionsCell {
  id: number
  name: string
}
const ActionsCell: FC<IActionsCell> = ({ id, name }) => {
  const classes = useStyles()

  return (
    <div className={classes.cellElLink}>
      <Link
        to={`/developments/${id}`}
        onClick={() => {
          mixpanelTrack('DEVELOPMENTS_VIEW_DEVELOPMENT', {
            value: name,
          })
        }}
      >
        Edit
      </Link>
    </div>
  )
}

interface ITitleProps {
  name: string
}
const TitleWrapper: FC<ITitleProps> = ({ name }) => {
  const classes = useStyles()
  return <Typography className={classes.title}>{name}</Typography>
}

function createData(development, canEditDevelopment) {
  const {
    id,
    rating,
    name,
    developers,
    town,
    current_ranking,
    ranked_out_of,
    no_of_reviews,
  } = development
  return [
    <React.Fragment key="id">{id}</React.Fragment>,
    <TitleWrapper key="title" name={name} />,
    <Rating
      key="rating"
      rating={rating}
      noReviews={no_of_reviews}
      fontSize={16}
    />,
    <React.Fragment key="developers">
      {developers?.map((developer: any) => developer?.name)?.join(', ')}
    </React.Fragment>,
    <AreaRanking
      key="area"
      area={town}
      currentRanking={current_ranking}
      rankedOutOf={ranked_out_of}
    />,
    <div
      key="actions"
      className={clsx(!canEditDevelopment(id) && 'h-editDevDisabled')}
    >
      <ActionsCell id={id} name={name} />
    </div>,
  ]
}

export const createRows = (developments: Development[], canEditDevelopment) => {
  if (!developments.length) {
    return []
  }
  return developments.map((development: Development) =>
    createData(development, canEditDevelopment),
  )
}

export const createSkeletonRows = () =>
  Array(20)
    .fill('0')
    .map((_, idx) =>
      Array(11)
        .fill('0')
        .map((i, index) => {
          return <Skeleton key={`${idx}-${index}`} />
        }),
    )
